/**
 * WeatherStationCreateModal — hosts the "Create New Station" modal for WSE.
 *
 * Listens for `wse:create-request` so any toolbar button in the explorer can
 * open the modal without prop drilling. Wraps StationForm in a dirty-state
 * guard so closing with unsaved changes prompts first.
 */

import React, { useCallback, useEffect, useState } from 'react';
import StationForm from './StationForm';
import AppModal from '../../common/AppModal';
import { useConfirm } from '../../common/AppConfirm';

interface WeatherStationCreateModalProps {
    config: any;
    onCreated?: () => void;
}

const WeatherStationCreateModal: React.FC<WeatherStationCreateModalProps> = ({ config, onCreated }) => {
    const askConfirm = useConfirm();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [stationFormDirty, setStationFormDirty] = useState(false);

    useEffect(() => {
        const handler = () => setShowCreateModal(true);
        window.addEventListener('wse:create-request', handler);
        return () => window.removeEventListener('wse:create-request', handler);
    }, []);

    const tryClose = useCallback(async () => {
        if (stationFormDirty) {
            if (await askConfirm({ title: 'Unsaved changes', message: 'You have unsaved changes. Are you sure you want to exit?', confirmLabel: 'Discard changes', destructive: true })) {
                setShowCreateModal(false);
                setStationFormDirty(false);
            }
        } else {
            setShowCreateModal(false);
        }
    }, [stationFormDirty]);

    useEffect(() => {
        if (!showCreateModal) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') tryClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showCreateModal, tryClose]);

    return (
        <AppModal
            isOpen={showCreateModal}
            onClose={tryClose}
            width={700}
            maxHeight="90vh"
            header="Create New Station"
            persistent
        >
            <StationForm
                onSave={async (data) => {
                    await config.serviceAdapter.createEntity(data);
                    setShowCreateModal(false);
                    setStationFormDirty(false);
                    onCreated?.();
                }}
                onCancel={() => { setShowCreateModal(false); setStationFormDirty(false); }}
                title="Create New Station"
                onDirtyChange={setStationFormDirty}
            />
        </AppModal>
    );
};

export default WeatherStationCreateModal;
